import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { capture } from "@/lib/analytics";
import { EVENTS } from "@/lib/analyticsEvents";

const MotionLink = motion(Link);

// Shared button micro-interaction: subtle grow on hover, press on tap.
const pressMotion = {
  whileHover: { scale: 1.03 },
  whileTap: { scale: 0.97 },
  transition: { type: "spring", stiffness: 400, damping: 25 },
};

// When `track` is set, a register-CTA click emits a single funnel event with the
// originating section. Buttons without `track` (e.g. "Consulter") emit nothing.
//
// Un seul événement paramétré, et non un par emplacement : `section` répond
// déjà à « quel bouton convertit », et un `hero_cta_clicked` distinct
// obligerait à additionner deux séries pour compter les clics au total.
const trackClick = (section) => {
  if (section) capture(EVENTS.REGISTER_CTA_CLICKED, { section });
};

export const MECHANISM_IMAGE =
  "https://images.unsplash.com/photo-1768062251809-739d987a42fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
export const ROSEGOLD_IMAGE =
  "https://images.unsplash.com/photo-1772949400107-f35fd026ab77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
// Warm lifestyle shots — a watch worn, not displayed. Used to humanise the
// text-heavy middle of the landing page.
export const WRIST_IMAGE =
  "https://images.unsplash.com/photo-1524805444758-089113d48a6d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
export const HANDS_IMAGE =
  "https://images.unsplash.com/photo-1547996160-81dfa63595aa?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export const EASE_LUXE = [0.22, 1, 0.36, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: EASE_LUXE },
  }),
};

// Scale-in reveal for cards and framed objects — reads as "placed down".
export const fadeScale = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, delay: i * 0.12, ease: EASE_LUXE },
  }),
};

// Lateral reveals for image/text two-column sections.
export const revealLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: EASE_LUXE } },
};
export const revealRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: EASE_LUXE } },
};

// Shared scroll-reveal viewport configs (avoids re-created inline objects in props).
export const VIEWPORT = { once: true, margin: "-100px" };
export const VIEWPORT_SM = { once: true, margin: "-60px" };

// Card-grid container: children (variants={fadeUp}) cascade in on scroll, 120ms apart.
export const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

export const Eyebrow = ({ children, testId }) => (
  <span
    data-testid={testId}
    className="inline-block text-xs md:text-sm tracking-[0.35em] uppercase text-gold-ink mb-6"
  >
    {children}
  </span>
);

const goldClass =
  "btn-sheen group relative overflow-hidden inline-flex items-center justify-center gap-3 bg-gold-strong text-on-gold px-8 py-4 min-h-[52px] text-sm font-semibold tracking-[0.15em] uppercase shadow-[0_8px_30px_-12px_rgb(var(--color-gold-strong)/0.6)] hover:bg-gold-muted hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_rgb(var(--color-gold-strong)/0.7)] transition-all duration-300";

const outlineClass =
  "group relative inline-flex items-center justify-center gap-3 border border-gold/50 text-gold-ink px-8 py-4 min-h-[52px] text-sm font-semibold tracking-[0.15em] uppercase hover:bg-gold/10 hover:border-gold hover:-translate-y-0.5 transition-all duration-300";

export const GoldButton = ({ children, href, to, testId, track }) => {
  const onClick = () => trackClick(track);
  const inner = (
    <>
      {children}
      <ArrowRight
        size={18}
        className="transition-transform duration-300 group-hover:translate-x-1"
      />
    </>
  );
  if (to)
    return (
      <MotionLink to={to} data-testid={testId} className={goldClass} onClick={onClick} {...pressMotion}>
        {inner}
      </MotionLink>
    );
  return (
    <motion.a href={href} data-testid={testId} className={goldClass} onClick={onClick} {...pressMotion}>
      {inner}
    </motion.a>
  );
};

// Discreet CTA — keeps the funnel event and test id without repeating a filled
// button at every scroll stop. `to` routes, `href` for same-page anchors.
export const TextCta = ({ children, href, to, testId, track }) => {
  const onClick = () => trackClick(track);
  const className =
    "link-underline group inline-flex items-center gap-2 text-sm font-medium tracking-[0.15em] uppercase text-gold-ink";
  const inner = (
    <>
      {children}
      <ArrowRight
        size={16}
        className="transition-transform duration-300 group-hover:translate-x-1"
      />
    </>
  );
  if (to)
    return (
      <Link to={to} data-testid={testId} onClick={onClick} className={className}>
        {inner}
      </Link>
    );
  return (
    <a href={href} data-testid={testId} onClick={onClick} className={className}>
      {inner}
    </a>
  );
};

export const OutlineButton = ({ children, href, to, testId, track }) => {
  const onClick = () => trackClick(track);
  const inner = (
    <>
      {children}
      <ArrowUpRight
        size={18}
        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </>
  );
  if (to)
    return (
      <MotionLink to={to} data-testid={testId} className={outlineClass} onClick={onClick} {...pressMotion}>
        {inner}
      </MotionLink>
    );
  return (
    <motion.a href={href} data-testid={testId} className={outlineClass} onClick={onClick} {...pressMotion}>
      {inner}
    </motion.a>
  );
};
